// src/ui/screens/chats/ConversationsScreen.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { chatService } from '../../../services/chat/chatService';
import { chatActionsService } from '../../../services/chat/chatActionsService';
import { chatLocalDb } from '../../../services/chat/chatLocalDb';
import { messageService } from '../../../services/chat/messageService';
import { ConversationTile } from './widgets/ConversationTile';
import { ArchiveBanner } from './widgets/ArchiveBanner';
import { TopBar } from './widgets/TopBar';
import { SearchBar } from './widgets/SearchBar';
import { TabBarSection } from './widgets/TabBarSection';
import { ChatScreen } from './ChatScreen';
import { db } from '../../../firebase';
import { collection, getDocs, query, where, doc, onSnapshot } from 'firebase/firestore';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  red: '#FF3B30',
};

const SEARCH_BAR_HEIGHT = 60;
const TAB_BAR_HEIGHT = 56;
const TOP_BAR_EXTRA_PADDING = 12;
const TOP_BAR_ROW_HEIGHT = 56;

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.filatom.app';

// Desktop layout constants — this screen has no category sidebar (unlike
// PostScreen), so the list pane only needs to clear the DesktopNavRail
// itself. DESKTOP_LIST_WIDTH is the fixed width of the left-hand
// conversation list in the split-pane desktop layout below.
const DESKTOP_RAIL_WIDTH = 76;
const DESKTOP_LIST_WIDTH = 400;

// ── Mute Duration Options ──────────────────────────────────────────────────

const MUTE_DURATIONS = [
  { label: '1 hour', duration: 3600000 },
  { label: '8 hours', duration: 28800000 },
  { label: '1 week', duration: 604800000 },
  { label: 'Always', duration: null },
];

// ── Open App Button ─────────────────────────────────────────────────────────

const OpenAppButton = () => {
  const handleOpenApp = () => {
    window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex flex-col items-center gap-2 mt-2">
      <button
        onClick={handleOpenApp}
        className="px-6 py-2.5 rounded-full text-black text-sm font-semibold shadow-lg transition-transform active:scale-95"
        style={{ backgroundColor: COLORS.orange }}
      >
        Open App
      </button>
      <span className="text-white/30 text-[11px]">Available for Android</span>
    </div>
  );
};

// ── ConversationsScreen ─────────────────────────────────────────────────────

export const ConversationsScreen = ({ onUnreadCountUpdated }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pinnedState, setPinnedState] = useState({});
  const [mutedState, setMutedState] = useState({});
  const [archivedState, setArchivedState] = useState({});
  const [blockedState, setBlockedState] = useState({});
  const [onlineState, setOnlineState] = useState({});
  const [deletedIds, setDeletedIds] = useState(new Set());
  const [searchBarProgress, setSearchBarProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Desktop split-view state: which conversation is currently shown in the
  // right-hand pane. Mobile never sets this — mobile taps always navigate
  // to the full /chat/:id route exactly as before.
  const [isDesktop, setIsDesktop] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);

  const scrollRef = useRef(null);
  const presenceSubs = useRef({});
  const deletedIdsRef = useRef(new Set());
  const unsubRef = useRef(null);
  const currentUserId = user?.uid || '';
  const initialLoadDone = useRef(false);

  // ── Desktop breakpoint detection ──────────────────────────────────────────

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const syncIsDesktop = (e) => setIsDesktop(e.matches);
    syncIsDesktop(mq);
    mq.addEventListener('change', syncIsDesktop);
    return () => mq.removeEventListener('change', syncIsDesktop);
  }, []);

  // ── Format time ────────────────────────────────────────────────────────────

  const formatTime = useCallback((date) => {
    if (!date) return '';
    const dt = date instanceof Date ? date : new Date(date);
    const now = new Date();
    const isToday = dt.getDate() === now.getDate() &&
      dt.getMonth() === now.getMonth() &&
      dt.getFullYear() === now.getFullYear();

    if (isToday) {
      let hours = dt.getHours();
      const minutes = dt.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours}:${minutes} ${ampm}`;
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (dt.getDate() === yesterday.getDate() &&
        dt.getMonth() === yesterday.getMonth() &&
        dt.getFullYear() === yesterday.getFullYear()) {
      return 'Yesterday';
    }

    const diffDays = Math.floor((now - dt) / (1000 * 60 * 60 * 24));
    if (diffDays < 7) {
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      return days[dt.getDay()];
    }

    return `${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
  }, []);

  // ── Enrich with user info ─────────────────────────────────────────────────

  const enrichWithUserInfo = useCallback(async (convos) => {
    const unknownIds = convos
      .filter(c => c.otherUserId && !c.otherUserName)
      .map(c => c.otherUserId)
      .filter(id => id);

    if (unknownIds.length === 0) return convos;

    const nameMap = {};
    const avatarMap = {};

    const chunkSize = 30;
    for (let i = 0; i < unknownIds.length; i += chunkSize) {
      const chunk = unknownIds.slice(i, i + chunkSize);
      try {
        const q = query(
          collection(db, 'users'),
          where('uid', 'in', chunk)
        );
        const snap = await getDocs(q);
        for (const doc of snap.docs) {
          const data = doc.data();
          nameMap[doc.id] = data.name || data.displayName || data.username || 'Unknown';
          avatarMap[doc.id] = data.profileImage || data.photoUrl || null;
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    }

    return convos.map(c => {
      if (nameMap[c.otherUserId]) {
        return {
          ...c,
          otherUserName: nameMap[c.otherUserId],
          otherUserAvatar: avatarMap[c.otherUserId],
        };
      }
      return c;
    });
  }, []);

  // ── Sync action states ────────────────────────────────────────────────────

  const syncActionStates = useCallback(async (convos) => {
    const pinned = {};
    const muted = {};
    const archived = {};
    const blocked = {};

    for (const c of convos) {
      try {
        pinned[c.id] = await chatActionsService.isPinned(c.id);
        muted[c.id] = await chatActionsService.isMuted(c.id);
        archived[c.id] = await chatActionsService.isArchived(c.id);
        if (c.otherUserId) {
          blocked[c.otherUserId] = await chatActionsService.isBlocked(c.otherUserId);
        }
      } catch (error) {
        console.error('Error syncing action states:', error);
      }
    }

    setPinnedState(pinned);
    setMutedState(muted);
    setArchivedState(archived);
    setBlockedState(blocked);

    // Seed archive cache
    const archivedIds = Object.keys(archived).filter(id => archived[id]);
    chatActionsService.seedArchiveCache(archivedIds);
  }, []);

  // ── Subscribe to presence ─────────────────────────────────────────────────

  const subscribePresenceForAll = useCallback((convos) => {
    for (const c of convos) {
      const otherId = c.otherUserId;
      if (!otherId || presenceSubs.current[otherId]) continue;

      const unsubscribe = onSnapshot(doc(db, 'presence', otherId), (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setOnlineState(prev => ({
            ...prev,
            [otherId]: data.isOnline || false,
          }));
        }
      }, (error) => {
        console.error('Presence subscription error:', error);
      });

      presenceSubs.current[otherId] = unsubscribe;
    }
  }, []);

  // ── Notify unread count ──────────────────────────────────────────────────

  const notifyUnread = useCallback((convos) => {
    const total = convos.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
    if (onUnreadCountUpdated) {
      onUnreadCountUpdated(total);
    }
  }, [onUnreadCountUpdated]);

  // ── Initialize ────────────────────────────────────────────────────────────

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      
      // Step 1: Load from local cache FIRST (instant display)
      try {
        // Preload conversations from cache
        const cached = await chatLocalDb.preloadConversations();
        
        if (cached && cached.length > 0) {
          const enriched = await enrichWithUserInfo(cached);
          await syncActionStates(enriched);
          
          // Update state immediately - this renders instantly
          setConversations(enriched);
          
          // Get archived count from cache
          const cachedArchiveCount = await chatLocalDb.archivedCount();
          setArchivedCount(cachedArchiveCount);
          
          notifyUnread(enriched);
          subscribePresenceForAll(enriched);
        }
      } catch (error) {
        console.error('Error loading cached conversations:', error);
      }

      // Step 2: Connect to Firestore and subscribe to real-time updates
      try {
        await chatService.connect();

        const unsubConversations = chatService.onConversations(async (incoming) => {
          // Resurrection detection
          const resurrectedIds = [];
          for (const c of incoming) {
            if (deletedIdsRef.current.has(c.id)) {
              resurrectedIds.push(c.id);
            }
          }
          for (const id of resurrectedIds) {
            deletedIdsRef.current.delete(id);
            await chatLocalDb.clearDeletedFlag(id);
          }

          // Filter out deleted conversations
          const toShow = incoming.filter(c => !deletedIdsRef.current.has(c.id));

          // Save to local DB and enrich
          await chatLocalDb.saveConversations(toShow);
          const enriched = await enrichWithUserInfo(toShow);
          await syncActionStates(enriched);

          // Seed archive cache
          const archivedIds = Object.keys(archivedState)
            .filter(id => archivedState[id])
            .map(id => id);
          chatActionsService.seedArchiveCache(archivedIds);

          // Filter archived conversations from main list
          const active = enriched.filter(c => !archivedState[c.id]);
          const archiveCount = enriched.filter(c => archivedState[c.id]).length;

          // Subscribe presence for new conversations
          subscribePresenceForAll(active);

          // Update state
          setConversations(active);
          setArchivedCount(archiveCount);
          setIsSyncing(false);
          notifyUnread(active);
          
          // Mark loading complete
          setIsLoading(false);
        });

        unsubRef.current = unsubConversations;

      } catch (error) {
        console.error('ConversationsScreen Firebase error:', error);
        setIsSyncing(false);
        setIsLoading(false);
      }
    };

    initialize();

    // Cleanup
    return () => {
      if (unsubRef.current) {
        unsubRef.current();
      }
      Object.values(presenceSubs.current).forEach(unsub => unsub());
      presenceSubs.current = {};
      chatService.disconnect();
    };
  }, []); // Empty dependency array - only run once on mount

  // ── Scroll handler ────────────────────────────────────────────────────────

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const offset = scrollRef.current.scrollTop;
    const progress = Math.min(offset / SEARCH_BAR_HEIGHT, 1);
    setSearchBarProgress(progress);
  }, []);

  // ── Selection handlers ────────────────────────────────────────────────────

  const enterSelectionMode = () => {
    setIsSelectionMode(true);
    setSelectedIds(new Set());
  };

  const exitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedIds(new Set());
  };

  const toggleSelection = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const getSelectedConversations = () => {
    return conversations.filter(c => selectedIds.has(c.id));
  };

  // ── Pin ────────────────────────────────────────────────────────────────────

  const handlePin = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const pinnedCount = ids.filter(id => pinnedState[id]).length;
    const shouldPin = pinnedCount < ids.length / 2;

    try {
      if (shouldPin) {
        await chatActionsService.pinMultiple(ids);
        setPinnedState(prev => {
          const newState = { ...prev };
          ids.forEach(id => newState[id] = true);
          return newState;
        });
      } else {
        await chatActionsService.unpinMultiple(ids);
        setPinnedState(prev => {
          const newState = { ...prev };
          ids.forEach(id => newState[id] = false);
          return newState;
        });
      }
      exitSelectionMode();
    } catch (error) {
      console.error('Pin error:', error);
    }
  };

  // ── Block ──────────────────────────────────────────────────────────────────

  const handleBlock = async () => {
    const selected = getSelectedConversations();
    if (selected.length === 0) return;

    const targets = selected.filter(c => c.otherUserId);
    if (targets.length === 0) return;

    const blockedCount = targets.filter(c => blockedState[c.otherUserId]).length;
    const shouldBlock = blockedCount < targets.length / 2;
    const names = targets.map(c => c.otherUserName || 'this user').join(', ');

    if (!window.confirm(
      shouldBlock
        ? `Block ${names}? They won't be able to send you messages.`
        : `Unblock ${names}? They will be able to send you messages again.`
    )) return;

    try {
      for (const c of targets) {
        if (shouldBlock) {
          await chatActionsService.blockUser(c.otherUserId);
          setBlockedState(prev => ({ ...prev, [c.otherUserId]: true }));
        } else {
          await chatActionsService.unblockUser(c.otherUserId);
          setBlockedState(prev => ({ ...prev, [c.otherUserId]: false }));
        }
      }
      exitSelectionMode();
    } catch (error) {
      console.error('Block error:', error);
    }
  };

  // ── Mute ──────────────────────────────────────────────────────────────────

  const handleMute = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const mutedCount = ids.filter(id => mutedState[id]).length;
    const shouldMute = mutedCount < ids.length / 2;

    if (!shouldMute) {
      try {
        await chatActionsService.unmuteMultiple(ids);
        setMutedState(prev => {
          const newState = { ...prev };
          ids.forEach(id => newState[id] = false);
          return newState;
        });
        exitSelectionMode();
        return;
      } catch (error) {
        console.error('Unmute error:', error);
        return;
      }
    }

    // Show mute duration options
    const duration = prompt(
      'Mute notifications for:\n1 hour\n8 hours\n1 week\nAlways\n\nEnter: 1h, 8h, 1w, always'
    );
    if (!duration) return;

    let selectedDuration = null;
    let label = '';
    if (duration.toLowerCase() === '1h' || duration === '1') {
      selectedDuration = 3600000;
      label = '1 hour';
    } else if (duration.toLowerCase() === '8h' || duration === '2') {
      selectedDuration = 28800000;
      label = '8 hours';
    } else if (duration.toLowerCase() === '1w' || duration === '3') {
      selectedDuration = 604800000;
      label = '1 week';
    } else if (duration.toLowerCase() === 'always' || duration === '4') {
      selectedDuration = null;
      label = 'Always';
    } else {
      return;
    }

    try {
      await chatActionsService.muteMultiple(ids, selectedDuration);
      setMutedState(prev => {
        const newState = { ...prev };
        ids.forEach(id => newState[id] = true);
        return newState;
      });
      exitSelectionMode();
    } catch (error) {
      console.error('Mute error:', error);
    }
  };

  // ── Archive ────────────────────────────────────────────────────────────────

  const handleArchive = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const archivedCountSelected = ids.filter(id => archivedState[id]).length;
    const shouldArchive = archivedCountSelected < ids.length / 2;

    try {
      if (shouldArchive) {
        await chatActionsService.archiveMultiple(ids);
        setArchivedState(prev => {
          const newState = { ...prev };
          ids.forEach(id => newState[id] = true);
          return newState;
        });
        setConversations(prev => prev.filter(c => !ids.includes(c.id)));
        setArchivedCount(prev => prev + ids.length);
      } else {
        await chatActionsService.unarchiveMultiple(ids);
        setArchivedState(prev => {
          const newState = { ...prev };
          ids.forEach(id => newState[id] = false);
          return newState;
        });
        setArchivedCount(prev => Math.max(0, prev - ids.length));
      }
      exitSelectionMode();
    } catch (error) {
      console.error('Archive error:', error);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    if (!window.confirm(
      ids.length === 1
        ? 'Delete this conversation? This cannot be undone.'
        : `Delete ${ids.length} conversations? This cannot be undone.`
    )) return;

    const participantsMap = {};
    for (const c of getSelectedConversations()) {
      participantsMap[c.id] = [currentUserId, c.otherUserId];
    }

    try {
      setConversations(prev => prev.filter(c => !ids.includes(c.id)));
      setDeletedIds(prev => new Set([...prev, ...ids]));
      notifyUnread(conversations.filter(c => !ids.includes(c.id)));

      if (ids.includes(selectedConversationId)) {
        setSelectedConversationId(null);
      }

      for (const id of ids) {
        await chatLocalDb.markConversationDeleted(id);
      }
      await chatActionsService.deleteMultiple(ids, participantsMap);
      exitSelectionMode();
    } catch (error) {
      console.error('Delete error:', error);
      const recovered = await chatLocalDb.loadConversations();
      if (recovered) {
        const enriched = await enrichWithUserInfo(recovered);
        setConversations(enriched);
        setDeletedIds(prev => {
          const newSet = new Set(prev);
          ids.forEach(id => newSet.delete(id));
          return newSet;
        });
        notifyUnread(enriched);
      }
    }
  };

  // ── Mark as read ──────────────────────────────────────────────────────────

  const handleMarkAsRead = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    try {
      await chatActionsService.markMultipleAsRead(ids);
      for (const id of ids) {
        await chatLocalDb.markConversationRead(id);
      }
      setConversations(prev => prev.map(c =>
        ids.includes(c.id) ? { ...c, unreadCount: 0 } : c
      ));
      notifyUnread(conversations.map(c =>
        ids.includes(c.id) ? { ...c, unreadCount: 0 } : c
      ));
      exitSelectionMode();
    } catch (error) {
      console.error('Mark as read error:', error);
    }
  };

  // ── Build conversation item ──────────────────────────────────────────────

  const buildConversationItem = useCallback((conversation) => {
    const otherId = conversation.otherUserId;
    const name = conversation.otherUserName || 'Loading...';
    const avatar = conversation.otherUserAvatar;
    const selected = selectedIds.has(conversation.id);
    const isPinned = pinnedState[conversation.id] || false;
    const isMuted = mutedState[conversation.id] || false;
    const isBlocked = blockedState[otherId] || false;
    const isOnline = onlineState[otherId] || false;
    const iLastMine = conversation.lastMessageSenderId === currentUserId;
    const isActiveDesktop = isDesktop && selectedConversationId === conversation.id;

    const handleLongPress = () => {
      if (isSelectionMode) {
        toggleSelection(conversation.id);
        return;
      }
    };

    const handleTap = () => {
      if (isSelectionMode) {
        toggleSelection(conversation.id);
        return;
      }

      // Optimistic update - mark as read immediately
      setConversations(prev => prev.map(c =>
        c.id === conversation.id ? { ...c, unreadCount: 0 } : c
      ));
      notifyUnread(conversations.map(c =>
        c.id === conversation.id ? { ...c, unreadCount: 0 } : c
      ));

      // Desktop: show the conversation inline in the right-hand pane
      // instead of navigating away — the standard split-pane chat UX
      // (WhatsApp Web / Telegram Desktop). Mobile is unchanged and still
      // navigates to the full /chat/:id route.
      if (isDesktop) {
        setSelectedConversationId(conversation.id);
      } else {
        navigate(`/chat/${conversation.id}`, {
          state: {
            receiverId: otherId,
            title: name,
            avatarUrl: avatar,
          },
        });
      }

      // Background operations
      chatLocalDb.markConversationRead(conversation.id).catch(() => {});
      chatActionsService.markAsRead(conversation.id).catch(() => {});
    };

    return (
      <div
        key={conversation.id}
        className={`lg:rounded-lg lg:mx-2 transition-colors ${
          isActiveDesktop ? 'lg:bg-white/[0.06]' : ''
        }`}
      >
        <ConversationTile
          name={name}
          lastMessage={isBlocked ? '🚫 Blocked' : (conversation.lastMessage || 'Say hello! 👋')}
          time={formatTime(conversation.lastMessageTime)}
          avatarUrl={avatar}
          unreadCount={isMuted ? 0 : (conversation.unreadCount || 0)}
          isPinned={isPinned}
          isMuted={isMuted}
          isBlocked={isBlocked}
          isOnline={isOnline}
          lastMessageStatus={iLastMine ? conversation.lastMessageStatus : null}
          iLastMessageMine={iLastMine}
          isSelectionMode={isSelectionMode}
          isSelected={selected}
          onSelectionToggle={() => toggleSelection(conversation.id)}
          onLongPress={handleLongPress}
          onTap={handleTap}
        />
      </div>
    );
  }, [selectedIds, pinnedState, mutedState, blockedState, onlineState, currentUserId, isSelectionMode, conversations, navigate, notifyUnread, formatTime, isDesktop, selectedConversationId]);

  // ── Render chats tab ──────────────────────────────────────────────────────

  const renderChatsTab = useCallback((topPadding) => {
    const visible = conversations.filter(c => !archivedState[c.id]);

    visible.sort((a, b) => {
      const aPinned = pinnedState[a.id] ? 0 : 1;
      const bPinned = pinnedState[b.id] ? 0 : 1;
      if (aPinned !== bPinned) return aPinned - bPinned;
      return new Date(b.lastMessageTime) - new Date(a.lastMessageTime);
    });

    if (isLoading) {
      return (
        <div
          className="h-full overflow-y-auto flex items-center justify-center"
          style={{ paddingTop: topPadding, paddingBottom: 120 }}
        >
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-white/35 text-sm">Loading conversations...</p>
            <p className="text-white/35 text-sm">Messaging is fast with the App</p>
            <OpenAppButton />
          </div>
        </div>
      );
    }

    if (visible.length === 0 && archivedCount === 0) {
      return (
        <div
          className="h-full overflow-y-auto"
          style={{ paddingTop: topPadding, paddingBottom: 120 }}
        >
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <svg className="w-16 h-16 text-white/12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-white/35 text-sm font-medium">No conversations yet</p>
            <p className="text-white/20 text-xs">Use App for faster messaging</p>
            <OpenAppButton />
          </div>
        </div>
      );
    }

    return (
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-full overflow-y-auto"
        style={{ paddingTop: topPadding, paddingBottom: 120 }}
      >
        {archivedCount > 0 && (
          <ArchiveBanner
            archivedCount={archivedCount}
            onTap={() => navigate('/archive')}
          />
        )}
        {visible.map(c => buildConversationItem(c))}
      </div>
    );
  }, [conversations, archivedState, pinnedState, isLoading, archivedCount, navigate, buildConversationItem, handleScroll]);

  // ── Render groups tab ─────────────────────────────────────────────────────

  const renderGroupsTab = useCallback((topPadding) => {
    return (
      <div
        className="h-full flex items-center justify-center"
        style={{ paddingTop: topPadding }}
      >
        <div className="flex flex-col items-center gap-4">
          <svg className="w-16 h-16 text-white/12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p className="text-white/35 text-sm font-medium">Groups coming soon</p>
        </div>
      </div>
    );
  }, []);

  // ── Main render ───────────────────────────────────────────────────────────

  const statusBarHeight = 0;
  const bottomNavHeight = 80 + 16;
  const headerHeight = statusBarHeight + TOP_BAR_ROW_HEIGHT + TOP_BAR_EXTRA_PADDING + SEARCH_BAR_HEIGHT + TAB_BAR_HEIGHT + 8;
  const searchBarOffset = SEARCH_BAR_HEIGHT * searchBarProgress;
  const gradientHeight = headerHeight - searchBarOffset;

  const selectedIdsArray = Array.from(selectedIds);
  const isPinnedMajority = selectedIdsArray.length > 0 &&
    selectedIdsArray.filter(id => pinnedState[id]).length >= selectedIdsArray.length / 2;
  const isMutedMajority = selectedIdsArray.length > 0 &&
    selectedIdsArray.filter(id => mutedState[id]).length >= selectedIdsArray.length / 2;
  const isArchivedMajority = selectedIdsArray.length > 0 &&
    selectedIdsArray.filter(id => archivedState[id]).length >= selectedIdsArray.length / 2;
  const isBlockedMajority = selectedIdsArray.length > 0 &&
    getSelectedConversations().filter(c => blockedState[c.otherUserId]).length >= selectedIdsArray.length / 2;

  const activeConversation = selectedConversationId
    ? conversations.find(c => c.id === selectedConversationId)
    : null;

  return (
    <div className="min-h-screen bg-filatom-bg lg:pl-[76px] lg:flex">
      {/* Left pane — the conversation list. On mobile (`lg:` inactive)
          this is simply the full-width screen exactly as before: every
          child below (tab content, gradient, sticky header, FAB) is
          completely unchanged from the original mobile markup, just
          re-scoped to this wrapper instead of the page root so it can
          sit beside a preview pane on desktop. */}
      <div className="relative w-full lg:w-[400px] lg:flex-shrink-0 lg:border-r lg:border-white/5">
        {/* Tab content */}
        <div className="h-screen">
          {renderChatsTab(headerHeight)}
        </div>

        {/* Gradient overlay */}
        <div
          className="absolute top-0 left-0 right-0 pointer-events-none"
          style={{
            height: gradientHeight,
            background: `linear-gradient(to bottom, rgba(8,9,11,0.95) 0%, rgba(8,9,11,0.75) 60%, rgba(8,9,11,0) 100%)`,
          }}
        />

        {/* Sticky header */}
        <div className="absolute top-0 left-0 right-0">
          <TopBar
            isSelectionMode={isSelectionMode}
            selectedCount={selectedIdsArray.length}
            onEditPressed={enterSelectionMode}
            onSelectionClose={exitSelectionMode}
            isPinned={isPinnedMajority}
            isMuted={isMutedMajority}
            isArchived={isArchivedMajority}
            isBlocked={isBlockedMajority}
            onMarkAsRead={handleMarkAsRead}
            onPin={handlePin}
            onBlock={handleBlock}
            onMute={handleMute}
            onArchive={handleArchive}
            onDelete={handleDelete}
          />

          <div
            className="transition-all duration-200"
            style={{
              transform: `translateY(-${searchBarOffset}px)`,
              opacity: 1 - searchBarProgress,
            }}
          >
            <SearchBar />
          </div>

          <div
            className="transition-all duration-200"
            style={{
              transform: `translateY(-${searchBarOffset}px)`,
              opacity: 1 - searchBarProgress,
            }}
          >
            <TabBarSection
              tabs={['Chats', 'Groups']}
              onTabChange={(index) => {
                // Handle tab change
              }}
            />
          </div>
        </div>

        {/* FAB — mobile only. Desktop drops the floating "get the app"
            button in favor of the split-pane chat to the right; the same
            Android prompt still appears inline via OpenAppButton in the
            loading/empty list states above. */}
        {!isSelectionMode && (
          <div
            className="absolute right-4 z-10 lg:hidden"
            style={{ bottom: bottomNavHeight }}
          >
            <button
              onClick={() => window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer')}
              className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
              style={{ backgroundColor: COLORS.orange }}
            >
              <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Right pane — desktop-only chat, WhatsApp-Desktop style: once a
          conversation tile is tapped, this shows the actual ChatScreen
          (embedded — no letterboxing, no back button, its "more" menu
          gets a "Close chat" item) instead of a lightweight preview.
          "Close chat" resets selectedConversationId back to null, which
          brings back the neutral "Select a conversation" placeholder. */}
      <div className="hidden lg:flex flex-1 flex-col bg-filatom-bg lg:h-screen">
        {activeConversation ? (
          <ChatScreen
            embedded
            conversationId={activeConversation.id}
            receiverId={activeConversation.otherUserId}
            title={activeConversation.otherUserName}
            avatarUrl={activeConversation.otherUserAvatar}
            onClose={() => setSelectedConversationId(null)}
          />
        ) : (
          <div className="h-full flex flex-col items-center justify-center gap-3 px-8 text-center">
            <svg className="w-20 h-20 text-white/10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-white/35 text-sm font-medium">Select a conversation</p>
            <p className="text-white/20 text-xs max-w-[220px]">
              Choose a chat from the list to preview it here
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationsScreen;